import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays, CheckCircle2, Link2, PenSquare,
  Clock, AlertTriangle, ArrowRight, Loader2, CreditCard,
  Play, FileText,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { cn } from '../lib/cn';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { AccountAvatar } from '../components/AccountAvatar';
import { OnboardingModal, hasSeenOnboarding } from '../components/OnboardingModal';
import { trackSignup } from '../lib/analytics';

/* ── Types ──────────────────────────────────────────────── */
type PostStatus = 'draft' | 'scheduled' | 'publishing' | 'published' | 'partial_failed' | 'failed' | 'canceled';

type Post = {
  id: string;
  caption: string;
  media: { url: string; type?: string; thumbnailUrl?: string | null }[];
  status: PostStatus;
  scheduledAt: string | null;
  publishedAt: string | null;
  targets: {
    socialAccountId: string;
    platform: string;
    username: string | null;
    displayName: string | null;
    profilePhotoUrl: string | null;
  }[];
};

type Account = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
  status: 'connected' | 'disconnected';
};

type SubInfo = {
  entitled: boolean;
  status: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  plan: { name: string } | null;
};

/* ── Helpers ────────────────────────────────────────────── */
function LiveClock({ tz }: { tz: string }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const weekday = now.toLocaleDateString('es-ES', { weekday: 'long', timeZone: tz });
  const day     = now.toLocaleDateString('es-ES', { day: 'numeric', timeZone: tz });
  const month   = now.toLocaleDateString('es-ES', { month: 'long', timeZone: tz });
  const time    = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: tz });

  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  return <span>Es {cap(weekday)}, {day} de {month} y son las {time}</span>;
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 13) return 'Buenos días';
  if (h < 21) return 'Buenas tardes';
  return 'Buenas noches';
}

function fmtDate(iso: string, tz: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: tz,
  });
}

function fmtTime(iso: string, tz: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz,
  });
}

function relativeFromNow(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  const mins  = Math.floor(Math.abs(diff) / 60_000);
  const hours = Math.floor(Math.abs(diff) / 3_600_000);
  const days  = Math.floor(Math.abs(diff) / 86_400_000);
  if (diff < 0) return 'hace un momento';
  if (mins < 60)  return `en ${mins} min`;
  if (hours < 24) return `en ${hours} h`;
  return `en ${days} d`;
}

function daysLeft(iso: string | null): number {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
}

/* ── Main component ─────────────────────────────────────── */
export function Dashboard() {
  const { user } = useAuth();
  const tz = user?.timezone ?? 'Europe/Madrid';

  // Team members can only call /api/posts if granted the section — skip otherwise
  // so the home doesn't hit a 403.
  const canSeePosts =
    user?.team?.role !== 'member' ||
    user.team.sections.includes('posts') ||
    user.team.sections.includes('post');

  const { data: postsData, isLoading: loadingPosts } = useQuery({
    queryKey: ['posts'],
    queryFn: () => api<{ posts: Post[] }>('/api/posts'),
    enabled: canSeePosts,
  });

  const { data: accountsData, isLoading: loadingAccounts } = useQuery({
    queryKey: ['social', 'accounts'],
    queryFn: () => api<{ accounts: Account[] }>('/api/social/accounts'),
  });

  const { data: subData } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => api<{ subscription: SubInfo | null }>('/api/billing/subscription'),
  });

  const posts    = postsData?.posts    ?? [];
  const accounts = accountsData?.accounts ?? [];
  const sub      = subData?.subscription ?? null;

  // Derived stats
  const scheduled  = posts.filter((p) => p.status === 'scheduled');
  const now7d      = Date.now() - 7 * 86_400_000;
  const published7 = posts.filter(
    (p) => p.status === 'published' && p.publishedAt && new Date(p.publishedAt).getTime() > now7d,
  );
  const connected  = accounts.filter((a) => a.status === 'connected');

  // First-run onboarding: show once to users who land here with no connected
  // account and haven't dismissed it. We wait for the accounts query so we
  // don't flash it before knowing whether they already have accounts.
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => {
    if (user?.id && !loadingAccounts && connected.length === 0 && !hasSeenOnboarding(user.id)) {
      setShowOnboarding(true);
    }
  }, [user?.id, loadingAccounts, connected.length]);

  // Google OAuth signups land here with ?signup=google — fire the registration
  // conversion once (GA sign_up + TikTok CompleteRegistration) and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') === 'google') {
      trackSignup('google', user?.id ? `reg_${user.id}` : undefined);
      params.delete('signup');
      const qs = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  // Next 5 upcoming scheduled posts (soonest first)
  const upcoming = [...scheduled]
    .filter((p) => p.scheduledAt)
    .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime())
    .slice(0, 5);

  // Failed/partial posts needing attention
  const failed = posts.filter((p) => p.status === 'failed' || p.status === 'partial_failed');

  // Prefer the real name (first word); fall back to the email local part.
  const firstNameOrEmail =
    user?.name?.trim().split(/\s+/)[0] || (user?.email ?? '').split('@')[0] || '';

  return (
    <div className="space-y-7">
      {showOnboarding && user?.id && (
        <OnboardingModal userId={user.id} onClose={() => setShowOnboarding(false)} />
      )}

      {/* ── Greeting ── */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {greeting()}, <span className="text-brand-600">{firstNameOrEmail}</span> 👋
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            <LiveClock tz={tz} />
          </p>
        </div>
        <Link to="/dashboard/posts/new" className="btn-primary gap-2 shrink-0">
          <PenSquare size={15} /> Nueva publicación
        </Link>
      </div>

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<CalendarDays size={18} />}
          iconBg="bg-amber-50 text-amber-600"
          label="Programadas"
          value={loadingPosts ? null : scheduled.length}
          sub="publicaciones pendientes"
          href="/dashboard/posts"
        />
        <StatCard
          icon={<CheckCircle2 size={18} />}
          iconBg="bg-emerald-50 text-emerald-600"
          label="Publicadas"
          value={loadingPosts ? null : published7.length}
          sub="últimos 7 días"
          href="/dashboard/posts"
        />
        <StatCard
          icon={<Link2 size={18} />}
          iconBg="bg-blue-50 text-blue-600"
          label="Cuentas conectadas"
          value={loadingAccounts ? null : connected.length}
          sub="redes sociales activas"
          href="/dashboard/accounts"
        />
        <PlanStatCard sub={sub} />
      </div>

      {/* ── Alert: failed posts ── */}
      {failed.length > 0 && (
        <Link
          to="/dashboard/posts?tab=error"
          className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 hover:bg-red-100 transition"
        >
          <AlertTriangle size={16} className="shrink-0 text-red-500" />
          <span>
            <span className="font-semibold">{failed.length} publicación{failed.length > 1 ? 'es' : ''} con error.</span>{' '}
            Revísalas y republícalas si es necesario.
          </span>
          <ArrowRight size={14} className="ml-auto shrink-0" />
        </Link>
      )}

      {/* ── Main grid ── */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upcoming posts — 2/3 */}
        <div className="lg:col-span-2 space-y-3">
          <SectionHeader title="Próximas publicaciones" href="/dashboard/posts" />
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {loadingPosts ? (
              <EmptyState icon={<Loader2 size={20} className="animate-spin text-slate-300" />} text="Cargando…" />
            ) : upcoming.length === 0 ? (
              <EmptyState
                icon={<CalendarDays size={22} className="text-slate-300" strokeWidth={1.5} />}
                text="No tienes publicaciones programadas."
                cta={{ label: 'Crear publicación', href: '/dashboard/posts/new' }}
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {upcoming.map((post) => {
                  const thumb = post.media?.[0];
                  const isVideo = thumb?.type === 'video';
                  const firstTarget = post.targets[0];
                  const meta = firstTarget ? PLATFORM_META[firstTarget.platform as Platform] : undefined;
                  return (
                    <li key={post.id}>
                      <Link
                        to={`/dashboard/posts/${post.id}/edit`}
                        className="flex items-center gap-3.5 px-4 py-3.5 hover:bg-slate-50 transition"
                      >
                        {/* thumbnail */}
                        <div className="relative h-12 w-12 rounded-lg bg-slate-100 shrink-0 overflow-hidden flex items-center justify-center">
                          {thumb ? (
                            isVideo ? (
                              <>
                                <video
                                  src={`${thumb.url}#t=0.5`}
                                  muted
                                  playsInline
                                  preload="metadata"
                                  className="h-full w-full object-cover"
                                />
                                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                                  <Play size={14} className="text-white drop-shadow" />
                                </span>
                              </>
                            ) : (
                              <img src={thumb.thumbnailUrl ?? thumb.url} alt="" loading="lazy" className="h-full w-full object-cover" />
                            )
                          ) : (
                            <FileText size={16} className="text-slate-400" />
                          )}
                        </div>

                        {/* main */}
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm truncate', post.caption ? 'text-slate-800' : 'text-slate-400 italic')}>
                            {post.caption || 'Sin texto'}
                          </p>
                          <div className="mt-1.5 flex items-center gap-2 min-w-0">
                            {post.targets.length > 0 && (
                              <div className="flex -space-x-1.5 shrink-0">
                                {post.targets.slice(0, 4).map((t) => (
                                  <span key={t.socialAccountId} className="ring-2 ring-white rounded-full inline-flex">
                                    <AccountAvatar
                                      account={{
                                        platform: t.platform as Platform,
                                        username: t.username,
                                        displayName: t.displayName,
                                        profilePhotoUrl: t.profilePhotoUrl,
                                      }}
                                      size={20}
                                    />
                                  </span>
                                ))}
                              </div>
                            )}
                            <span className="text-xs text-slate-500 truncate">
                              {firstTarget
                                ? firstTarget.displayName || firstTarget.username || meta?.label || ''
                                : ''}
                              {post.targets.length > 1 && (
                                <span className="text-slate-400"> +{post.targets.length - 1}</span>
                              )}
                            </span>
                          </div>
                        </div>

                        {/* date */}
                        <div className="text-right shrink-0">
                          <p className="text-xs font-medium text-slate-600">{fmtDate(post.scheduledAt!, tz)}</p>
                          <p className="text-[11px] text-slate-400">{fmtTime(post.scheduledAt!, tz)}</p>
                          <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-1.5 py-0.5">
                            <Clock size={9} />
                            {relativeFromNow(post.scheduledAt!)}
                          </span>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Connected accounts — 1/3 */}
        <div className="space-y-3">
          <SectionHeader title="Cuentas conectadas" href="/dashboard/accounts" />
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {loadingAccounts ? (
              <EmptyState icon={<Loader2 size={20} className="animate-spin text-slate-300" />} text="Cargando…" />
            ) : connected.length === 0 ? (
              <EmptyState
                icon={<Link2 size={22} className="text-slate-300" strokeWidth={1.5} />}
                text="No tienes cuentas conectadas."
                cta={{ label: 'Conectar cuenta', href: '/dashboard/accounts' }}
              />
            ) : (
              <ul className="divide-y divide-slate-50">
                {connected.map((acc) => {
                  const meta = PLATFORM_META[acc.platform];
                  if (!meta) return null;
                  return (
                    <li key={acc.id} className="flex items-center gap-3 px-4 py-3">
                      <AccountAvatar account={acc} size={32} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-700 truncate">
                          {acc.displayName ?? acc.username ?? meta.label}
                        </p>
                        <p className="text-xs text-slate-400 truncate">{meta.label}</p>
                      </div>
                      <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" title="Conectada" />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ── Quick actions ── */}
      <div>
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Acciones rápidas</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <QuickAction
            icon={<PenSquare size={18} />}
            iconBg="bg-brand-50 text-brand-600"
            title="Crear publicación"
            desc="Redacta y programa un nuevo post"
            href="/dashboard/posts/new"
          />
          <QuickAction
            icon={<CalendarDays size={18} />}
            iconBg="bg-amber-50 text-amber-600"
            title="Ver calendario"
            desc="Visualiza tu contenido programado"
            href="/dashboard/calendar"
          />
          <QuickAction
            icon={<Link2 size={18} />}
            iconBg="bg-blue-50 text-blue-600"
            title="Conectar red social"
            desc="Añade o gestiona tus cuentas"
            href="/dashboard/accounts"
          />
        </div>
      </div>
    </div>
  );
}

/* ── Stat card ──────────────────────────────────────────── */
function StatCard({
  icon, iconBg, label, value, sub, href,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: number | null;
  sub: string;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col gap-3 hover:shadow-md hover:border-slate-300 transition group"
    >
      <div className={cn('h-9 w-9 rounded-xl flex items-center justify-center', iconBg)}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-extrabold text-slate-900">
          {value === null ? <span className="text-slate-300 text-lg">…</span> : value}
        </p>
        <p className="text-xs font-semibold text-slate-600 mt-0.5">{label}</p>
        <p className="text-xs text-slate-400">{sub}</p>
      </div>
    </Link>
  );
}

/* ── Plan stat card ─────────────────────────────────────── */
function PlanStatCard({ sub }: { sub: SubInfo | null }) {
  const isActive   = sub?.entitled && sub.status === 'active';
  const isTrialing = sub?.entitled && sub.status === 'trialing';
  const days       = isTrialing ? Math.max(0, Math.ceil((new Date(sub!.trialEndsAt!).getTime() - Date.now()) / 86_400_000)) : 0;

  return (
    <Link
      to="/dashboard/billing"
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col gap-3 hover:shadow-md hover:border-slate-300 transition"
    >
      <div className={cn(
        'h-9 w-9 rounded-xl flex items-center justify-center',
        isActive ? 'bg-emerald-50 text-emerald-600' : isTrialing ? 'bg-brand-50 text-brand-600' : 'bg-slate-100 text-slate-400',
      )}>
        <CreditCard size={18} />
      </div>
      <div>
        {isActive && sub?.plan ? (
          <>
            <p className="text-lg font-extrabold text-slate-900 truncate">{sub.plan.name}</p>
            <p className="text-xs font-semibold text-emerald-600 mt-0.5">Plan activo</p>
            <p className="text-xs text-slate-400">Suscripción vigente</p>
          </>
        ) : isTrialing ? (
          <>
            <p className="text-2xl font-extrabold text-slate-900">{days}d</p>
            <p className="text-xs font-semibold text-brand-600 mt-0.5">Prueba gratuita</p>
            <p className="text-xs text-slate-400">
              {sub?.plan?.name ?? 'Plan de prueba'}
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-bold text-slate-500">Sin plan</p>
            <p className="text-xs font-semibold text-amber-600 mt-0.5">Suscríbete</p>
            <p className="text-xs text-slate-400">Activa tu cuenta</p>
          </>
        )}
      </div>
    </Link>
  );
}

/* ── Section header ─────────────────────────────────────── */
function SectionHeader({ title, href }: { title: string; href: string }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
      <Link to={href} className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-0.5 transition">
        Ver todo <ArrowRight size={12} />
      </Link>
    </div>
  );
}

/* ── Empty state ────────────────────────────────────────── */
function EmptyState({
  icon, text, cta,
}: {
  icon: React.ReactNode;
  text: string;
  cta?: { label: string; href: string };
}) {
  return (
    <div className="py-10 flex flex-col items-center gap-2 text-slate-400">
      {icon}
      <p className="text-sm">{text}</p>
      {cta && (
        <Link to={cta.href} className="mt-1 btn-primary text-xs gap-1.5 py-1.5">
          <PenSquare size={12} /> {cta.label}
        </Link>
      )}
    </div>
  );
}

/* ── Quick action card ──────────────────────────────────── */
function QuickAction({
  icon, iconBg, title, desc, href,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  desc: string;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-4 flex items-center gap-4 hover:shadow-md hover:border-slate-300 transition group"
    >
      <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center shrink-0', iconBg)}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-800 group-hover:text-brand-600 transition">{title}</p>
        <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
      </div>
      <ArrowRight size={14} className="ml-auto text-slate-300 group-hover:text-brand-400 transition shrink-0" />
    </Link>
  );
}
